import { toNestErrors } from '@hookform/resolvers';
import { type Namespace, type TFunction } from 'i18next';
import { type FieldError, type FieldValues, type Resolver } from 'react-hook-form';
import z from 'zod/v4';

import { nullToUndefined } from './form-value-utils';

export function makeIntegrationZodResolver<TValues extends FieldValues>(
  schema: z.ZodType<TValues>,
  t: TFunction<Namespace>,
): Resolver<TValues> {
  const normalized = z.preprocess(nullToUndefined, schema);

  return async (values, _context, options) => {
    const result = await normalized.safeParseAsync(values, {
      error: (issue) => messageFor(issue, t),
    });

    if (result.success) {
      return { values: result.data, errors: {} };
    }

    return { values: {}, errors: toNestErrors(collectErrors(result.error.issues, t), options) };
  };
}

function collectErrors(
  issues: z.core.$ZodIssue[],
  t: TFunction<Namespace>,
): Record<string, FieldError> {
  const errors: Record<string, FieldError> = {};
  const queue = [...issues];

  for (let issue = queue.shift(); issue; issue = queue.shift()) {
    const path = issue.path.join('.');
    const shown =
      issue.code === 'invalid_union' && issue.errors[0]?.[0] ? issue.errors[0][0] : issue;

    errors[path] ??= { message: finalMessage(shown, t), type: shown.code };

    if (issue.code === 'invalid_union') {
      issue.errors.forEach((branch) => queue.push(...branch));
    }
  }

  return errors;
}

/** A schema-level message wins over the parse error map in zod v4, so a custom issue's own key is read here, from the final issue. */
function finalMessage(issue: z.core.$ZodIssue, t: TFunction<Namespace>): string {
  const i18n = issue.code === 'custom' ? getI18nParam(issue.params) : undefined;
  return i18n ? t(i18n, { defaultValue: issue.message }) : issue.message;
}

/** Every rule a connector schema enforces, restated in the reader's language (field.md, "Messages"). */
function messageFor(issue: z.core.$ZodRawIssue, t: TFunction<Namespace>): string {
  switch (issue.code) {
    case 'invalid_value':
      return issue.input === undefined
        ? t('configureForm.validation.chooseOption')
        : t('configureForm.validation.chooseListedOption');
    case 'invalid_union':
      return t('configureForm.validation.chooseOption');
    case 'invalid_type':
      if (issue.input === undefined) {
        return issue.expected === 'array'
          ? t('configureForm.validation.addValue')
          : issue.expected === 'object'
            ? t('configureForm.validation.chooseOption')
            : t('configureForm.validation.required');
      }
      if (issue.expected === 'int' && typeof issue.input === 'number') {
        return t('configureForm.validation.notInteger');
      }
      if (['number', 'int', 'bigint', 'nan'].includes(issue.expected)) {
        return t('configureForm.validation.enterNumber');
      }
      if (issue.expected === 'date') {
        return t('configureForm.validation.enterDate');
      }
      if (issue.expected === 'string') {
        return t('configureForm.validation.enterText');
      }
      if (issue.expected === 'boolean') {
        return t('configureForm.validation.checkOrClear');
      }
      return t('configureForm.validation.invalid');
    case 'too_small':
      return boundMessage(issue, 'Small', issue.minimum, t);
    case 'too_big':
      return boundMessage(issue, 'Big', issue.maximum, t);
    case 'not_multiple_of':
      return t('configureForm.validation.notMultipleOf', { divisor: issue.divisor.toString() });
    case 'invalid_format':
      if (issue.format === 'url') return t('configureForm.validation.url');
      if (issue.format === 'email') return t('configureForm.validation.email');
      return t('configureForm.validation.format');
    default:
      return t('configureForm.validation.invalid');
  }
}

function boundMessage(
  issue: Extract<z.core.$ZodRawIssue, { code: 'too_small' | 'too_big' }>,
  Side: 'Small' | 'Big',
  bound: number | bigint | Date,
  t: TFunction<Namespace>,
): string {
  const exclusive = !issue.inclusive;

  if (issue.origin === 'string') {
    return t(`configureForm.validation.stringToo${Side}`, { count: Number(bound) });
  }
  if (issue.origin === 'array' || issue.origin === 'set') {
    return t(`configureForm.validation.too${Side}`, { count: Number(bound) });
  }
  if (issue.origin === 'date') {
    const value = bound instanceof Date ? bound.toISOString().slice(0, 10) : bound.toString();
    return t(`configureForm.validation.dateToo${Side}${exclusive ? 'Exclusive' : ''}`, {
      minimum: value,
      maximum: value,
    });
  }
  return t(`configureForm.validation.numberToo${Side}${exclusive ? 'Exclusive' : ''}`, {
    minimum: bound.toString(),
    maximum: bound.toString(),
  });
}

function getI18nParam(params: unknown): string | undefined {
  if (!params || typeof params !== 'object' || !('i18n' in params)) {
    return undefined;
  }

  const i18n = (params as Record<string, unknown>)['i18n'];
  return typeof i18n === 'string' ? i18n : undefined;
}
